import { Injectable, inject } from '@angular/core';
import { ApiContextService } from './../../services/api-context/api-context.service';
import { HttpService } from '@boels/helpers';
import { catchError, Observable, of, switchMap } from 'rxjs';
import {
  BoelsPointOfService,
  GeoPoint,
  ParamsModel,
  SpecialismList,
  StoreCountCountryPayload,
  StoreFinderSearchPage,
} from '@boels/models';

@Injectable({
  providedIn: 'root',
})
export class StoreFinderGateway {
  private apiContextService = inject(ApiContextService);
  private httpService = inject(HttpService);

  getPointOfService(
    pointOfServiceName: string,
  ): Observable<BoelsPointOfService> {
    return this.apiContextService.getContext().pipe(
      switchMap(([baseSite, language, currency]) => {
        const queryParams: ParamsModel = [
          ['fields', 'FULL'],
          ['lang', language],
          ['curr', currency],
        ];
        return this.httpService.get<BoelsPointOfService>(
          `${baseSite.uid}/stores/displayNameSlug/${pointOfServiceName}`,
          queryParams,
        );
      }),
    );
  }

  getPointOfServicesByQuery(
    query: string,
    pageSize = 20,
    specialism?: string,
  ): Observable<StoreFinderSearchPage> {
    return this.apiContextService.getContext().pipe(
      switchMap(([baseSite, language, currency]) => {
        const queryParams: ParamsModel = [
          ['fields', 'FULL'],
          ['lang', language],
          ['curr', currency],
          ['query', query],
          ['radius', '250000'],
          ['pageSize', pageSize],
        ];

        if (specialism) {
          queryParams.push(['specialism', specialism]);

          return this.httpService.get<StoreFinderSearchPage>(
            `${baseSite.uid}/stores/filter-by-specialism`,
            queryParams,
          );
        }
        return this.httpService.get<StoreFinderSearchPage>(
          `${baseSite.uid}/stores/`,
          queryParams,
        );
      }),
      catchError(() => {
        return of({ stores: [], sourceLatitude: 0, sourceLongitude: 0 });
      }),
    );
  }

  getPointOfServicesByGeoPoint(
    geoPoint: GeoPoint,
    pageSize = 20,
    specialism?: string,
  ): Observable<StoreFinderSearchPage> {
    return this.apiContextService.getContext().pipe(
      switchMap(([baseSite, language, currency]) => {
        const queryParams: ParamsModel = [
          ['fields', 'FULL'],
          ['lang', language],
          ['curr', currency],
          ['longitude', geoPoint.longitude ?? ''],
          ['latitude', geoPoint.latitude ?? ''],
          ['radius', '250000'],
          ['pageSize', pageSize],
        ];

        if (specialism) {
          queryParams.push(['specialism', specialism]);

          return this.httpService.get<StoreFinderSearchPage>(
            `${baseSite.uid}/stores/filter-by-specialism`,
            queryParams,
          );
        }
        return this.httpService.get<StoreFinderSearchPage>(
          `${baseSite.uid}/stores/`,
          queryParams,
        );
      }),
      catchError(() => {
        return of({
          stores: [],
          sourceLatitude: geoPoint.latitude,
          sourceLongitude: geoPoint.longitude,
        });
      }),
    );
  }

  getStoresByRegion(): Observable<StoreCountCountryPayload> {
    return this.apiContextService.getContext().pipe(
      switchMap(([baseSite, language, currency]) => {
        const queryParams: ParamsModel = [
          ['fields', 'FULL'],
          ['lang', language],
          ['curr', currency],
        ];
        return this.httpService.get<StoreCountCountryPayload>(
          `${baseSite.uid}/stores/storescounts`,
          queryParams,
        );
      }),
    );
  }

  getStoreFinderSpecialisms(): Observable<SpecialismList> {
    return this.apiContextService.getContext().pipe(
      switchMap(([baseSite, language, currency]) => {
        const queryParams: ParamsModel = [
          ['fields', 'FULL'],
          ['lang', language],
          ['curr', currency],
        ];

        return this.httpService.get<SpecialismList>(
          `${baseSite.uid}/stores/specialisms`,
          queryParams,
        );
      }),
    );
  }
}
